import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../api";
import { PageTitle, Badge } from "./AdminLayout";
import { useAuth } from "../../context/AuthContext";

const STATUS_META = {
  operational: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10",
  degraded: "text-amber-400 border-amber-400/30 bg-amber-400/10",
  outage: "text-red-400 border-red-400/30 bg-red-400/10",
  maintenance: "text-volt border-volt/30 bg-volt/10",
};

export default function StatusAdmin() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [title, setTitle] = useState("");
  const [updateText, setUpdateText] = useState({});

  const load = () => api.get("/public/status").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setComponent = async (id, status) => {
    try {
      await api.patch(`/admin/status/components/${id}`, { status });
      toast.success("Component updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const createIncident = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/status/incidents", { title });
      toast.success("Incident opened");
      setTitle("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const postUpdate = async (id, status) => {
    await api.patch(`/admin/status/incidents/${id}`, { text: updateText[id] || "", status });
    toast.success("Incident updated");
    setUpdateText({ ...updateText, [id]: "" });
    load();
  };

  if (!data) return <p className="text-white/40 font-mono text-sm">Loading…</p>;

  return (
    <div data-testid="admin-status">
      <PageTitle title="System Status" sub="Control what the public status page shows." />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-white/5 bg-panel overflow-hidden h-fit" data-testid="status-components-admin">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold px-5 py-4">Components</p>
          <div className="divide-y divide-white/5">
            {data.components.map((c) => (
              <div key={c.id} className="px-5 py-4 flex items-center justify-between gap-4" data-testid={`component-admin-${c.key}`}>
                <span className="text-sm font-semibold text-white">{c.name}</span>
                <select value={c.status} onChange={(e) => setComponent(c.id, e.target.value)} data-testid={`component-status-${c.key}`}
                  className="bg-white/5 border border-white/10 rounded-md px-3 py-1.5 text-xs text-white focus:outline-none [&>option]:bg-panel">
                  <option value="operational">Operational</option>
                  <option value="degraded">Degraded</option>
                  <option value="outage">Outage</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
            ))}
          </div>
          <form onSubmit={async (e) => {
            e.preventDefault();
            const name = e.target.elements.compname.value.trim();
            if (name.length < 2) return;
            try {
              await api.post("/admin/status/components", { name, key: name.toLowerCase().replace(/[^a-z0-9]+/g, "-") });
              toast.success("Component added");
              e.target.reset();
              load();
            } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
          }} className="flex gap-2 px-5 py-4 border-t border-white/5">
            <input name="compname" placeholder="Add component (e.g. Lyrics API)" data-testid="component-add-input"
              className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blurple" />
            <button type="submit" className="text-xs font-semibold text-volt border border-volt/30 rounded-md px-4 hover:bg-volt/10 transition-colors duration-150" data-testid="component-add-button">Add</button>
          </form>
        </div>

        <div className="space-y-4">
          <form onSubmit={createIncident} className="rounded-lg border border-white/5 bg-panel p-5 space-y-3" data-testid="incident-form">
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold">Open incident</p>
            <input value={title} onChange={(e) => setTitle(e.target.value)} required minLength={4} placeholder="e.g. EU music node degradation"
              className="w-full bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple" data-testid="incident-title-input" />
            <button type="submit" className="bg-amber-500/80 hover:bg-amber-500 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors duration-150" data-testid="incident-create">Open incident</button>
          </form>

          <div className="space-y-3" data-testid="incidents-admin-list">
            {data.incidents.map((inc) => (
              <div key={inc.id} className="rounded-lg border border-white/5 bg-panel p-5" data-testid={`incident-admin-${inc.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                  <p className="font-heading font-bold text-white text-sm">{inc.title}</p>
                  <Badge tone={inc.status === "resolved" ? "green" : "amber"}>{inc.status}</Badge>
                </div>
                {inc.status !== "resolved" && (
                  <div className="flex gap-2 mt-3">
                    <input value={updateText[inc.id] || ""} onChange={(e) => setUpdateText({ ...updateText, [inc.id]: e.target.value })} placeholder="Post an update…"
                      className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blurple" data-testid={`incident-update-${inc.id}`} />
                    <button onClick={() => postUpdate(inc.id, "investigating")} className="text-xs font-semibold text-volt border border-volt/30 rounded-full px-3 transition-colors duration-150" data-testid={`incident-post-${inc.id}`}>Post</button>
                    <button onClick={() => postUpdate(inc.id, "resolved")} className="text-xs font-semibold text-emerald-400 border border-emerald-400/30 rounded-full px-3 transition-colors duration-150" data-testid={`incident-resolve-${inc.id}`}>Resolve</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
